import ee from '../../services/earth-engine';
import { ENSEMBLE, GRADIENT_BOOST, kindsFor, RANDOM_FOREST } from './trees';
import { presetMismatch } from './preset';

/**
 * Saves a trained tree model to a file and loads it back, so a model fitted on one
 * date can be run against another without refitting.
 * The obvious route is `Export.classifier.toAsset`, and that is what held this up: it needs an
 * Earth Engine asset path, which means deciding where the team hosts models. It turns out not to
 * be needed. A trained ensemble can be read back as its decision trees - `classifier.explain()`
 * returns them as strings - and rebuilt from those with `ee.Classifier.decisionTreeEnsemble`. The
 * strings are plain text, so the model travels as a file the user downloads and uploads, with no
 * hosting and no account to share.
 * What the file has to carry besides the trees is everything needed to feed them the same inputs:
 * the predictor names in the same order, the imagery they were built from, and the scale. A tree
 * split on `SR_B3` applied to a Sentinel image would find a band of that name and predict
 * confidently from the wrong data, which is why the imagery is checked before the model is used -
 * see {@link modelMismatch}.
 */

/**
 * @constant MODEL_FILE_VERSION
 * @description Version of the saved format. Written into every file and checked on load, so a
 * file from a future format is refused rather than half-read.
 * @memberof module:Bathymetry
 */
export const MODEL_FILE_VERSION = 1;

/**
 * @constant MODEL_FILE_KIND
 * @description What the file is, so one dropped into the wrong importer is recognised as such.
 * @memberof module:Bathymetry
 */
export const MODEL_FILE_KIND = 'cassie-bathymetry-model';

/**
 * @function memberKey
 * @description The key an ensemble member's trees are stored under.
 * @param {String} kind - One of the tree model names.
 * @memberof module:Bathymetry
 * @returns {String} `'rf'` or `'gbt'`.
 */
export const memberKey = (kind) => (kind === GRADIENT_BOOST ? 'gbt' : 'rf');

/**
 * @function treesOf
 * @description The decision trees of a trained classifier, as the strings that can rebuild it.
 * @param {Object} classifier - A trained `ee.Classifier`.
 * **Called by:
 * @see {ProcessingPage} - when the user saves a model.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.List` of strings, still unevaluated.
 */
export const treesOf = (classifier) => ee.List(ee.Dictionary(classifier.explain()).get('trees'));

/**
 * @function buildModelFile
 * @description The saved model, as plain data ready to be written as JSON.
 * @param {Object} params
 * @param {String} params.model - Which tree model was fitted.
 * @param {Array<String>} params.predictors - The predictor names, in the order they were trained
 *  in. Order matters: the trees refer to them by name, but the stack is built by this list.
 * @param {Object} params.params - The calibration the model was fitted with.
 * @param {Object} params.trees - `{ rf: [...], gbt: [...] }`, already evaluated.
 * @param {Object} params.context - The imagery, as {@link presetContextOf} builds it.
 * @param {Object} [params.trainedOn] - What it was fitted on, for a reader of the file.
 * @memberof module:Bathymetry
 * @returns {Object} The file's contents.
 */
export const buildModelFile = ({ model, predictors, params, trees, context, trainedOn, exportedAt }) => ({
  kind: MODEL_FILE_KIND,
  version: MODEL_FILE_VERSION,
  exportedAt,
  model,
  predictors,
  predictorSet: params?.predictorSet,
  scale: params?.scale ?? null,
  ensembleWeightRf: params?.ensembleWeightRf ?? null,
  params: {
    rfNumberOfTrees: params?.rfNumberOfTrees,
    rfVariablesPerSplit: params?.rfVariablesPerSplit,
    rfMinLeafPopulation: params?.rfMinLeafPopulation,
    rfBagFraction: params?.rfBagFraction,
    gbtNumberOfTrees: params?.gbtNumberOfTrees,
    gbtShrinkage: params?.gbtShrinkage,
    gbtSamplingRate: params?.gbtSamplingRate,
    gbtMaxNodes: params?.gbtMaxNodes,
    gbtLoss: params?.gbtLoss,
    seed: params?.seed,
  },
  context,
  trainedOn: trainedOn || null,
  members: trees,
});

/**
 * @function readModelFile
 * @description Checks a parsed file is a model this app can use, and says why when it is not.
 * A file that is almost right is the dangerous case: a model with no predictor list, or with trees
 * for a member the chosen model does not have, would otherwise be applied and quietly predict from
 * the wrong inputs.
 * @param {Object} parsed - The parsed JSON.
 * @memberof module:Bathymetry
 * @returns {Object} `{ model }` when usable, `{ error }` with a reason key when not.
 */
export const readModelFile = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return { error: 'notJson' };
  if (parsed.kind !== MODEL_FILE_KIND) return { error: 'notAModel' };
  if (parsed.version !== MODEL_FILE_VERSION) return { error: 'version' };
  if (![RANDOM_FOREST, GRADIENT_BOOST, ENSEMBLE].includes(parsed.model)) return { error: 'unknownModel' };
  if (!Array.isArray(parsed.predictors) || !parsed.predictors.length) return { error: 'noPredictors' };

  const expected = kindsFor(parsed.model).map(memberKey);
  const missing = expected.filter((key) => !Array.isArray(parsed.members?.[key]) || !parsed.members[key].length);
  if (missing.length) return { error: 'noTrees' };

  return { model: parsed };
};

/**
 * @function classifiersFromModel
 * @description Rebuilds the trained ensembles from their saved trees.
 * The output mode is set to match how they were trained: these predict a depth, not a class, and a
 * rebuilt classifier left in classification mode returns the nearest training label instead.
 * @param {Object} saved - A file that passed {@link readModelFile}.
 * **Called by:
 * @see {ProcessingPage} - when a saved model is applied.
 * @memberof module:Bathymetry
 * @returns {Array<Object>} `ee.Classifier` per member, in the order {@link kindsFor} gives.
 */
export const classifiersFromModel = (saved) =>
  kindsFor(saved.model).map((kind) =>
    ee.Classifier.decisionTreeEnsemble(ee.List(saved.members[memberKey(kind)])).setOutputMode('REGRESSION'),
  );

/**
 * @function modelMismatch
 * @description Whether a saved model was built for imagery other than the one now selected.
 * Reuses the check the typed-in coefficients already go through, because the hazard is identical:
 * a model applied to the wrong imagery does not fail, it answers.
 * @param {Object} saved - A file that passed {@link readModelFile}.
 * @param {Object} current - The imagery in use, as `presetContextOf` builds it.
 * @memberof module:Bathymetry
 * @returns {Object|null} `{ expected, actual }` when they differ.
 * @see {@link presetMismatch}
 */
export const modelMismatch = (saved, current) => presetMismatch(saved?.context, current);

/**
 * @function missingPredictors
 * @description The predictors a saved model needs that the current imagery cannot provide.
 * The imagery check above catches a different satellite; this catches the subtler case of the same
 * satellite with a different predictor set chosen, where the names exist but the stack does not
 * build them.
 * @param {Object} saved - A file that passed {@link readModelFile}.
 * @param {Array<String>} available - The predictors the current settings would produce.
 * @memberof module:Bathymetry
 * @returns {Array<String>} Empty when the model can be applied.
 */
export const missingPredictors = (saved, available) => {
  const have = new Set(available || []);
  return (saved?.predictors || []).filter((name) => !have.has(name));
};
